import { useCallback, useEffect, useMemo } from "react";
import * as React from "react";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";

import MultiSelect from "../../../../MultiSelect";
import DateTimePicker from "../../../../DateTimePicker";
import {
    RESET_FILTERS,
    SET_SELECTED_CUSTOM_FILTER_CONFIG,
    SET_SELECTED_CUSTOM_FILTER_VALUES,
} from "../../reducers/reducersFilters/filters";

import type { UseDispatchHook } from "../../../../../lib/Util";
import type { ReduxState } from "../../reducers/reducers";
import type {
    PlanningTableFiltersActionTypes,
    SelectedCustomFilterConfig,
} from "../../reducers/reducersFilters/filters";

const CUSTOM_FILTER_TABS = {
    orders: "orders",
};

const CUSTOM_FILTER_ENTITIES = {
    material: "material",
    order: "order",
};

const CUSTOM_FILTER_INPUT_TYPES = {
    datetime: "datetime",
    select: "select",
    multiselect: "multiselect",
};

const CUSTOM_FILTER_MATCH = {
    exact: "exact",
    greater_than: "greater_than",
    greater_than_or_equal: "greater_than_or_equal",
    less_than: "less_than",
    less_than_or_equal: "less_than_or_equal",
};

type CustomFilterTab = keyof typeof CUSTOM_FILTER_TABS;
type CustomFilterEntity = keyof typeof CUSTOM_FILTER_ENTITIES;
type CustomFilterInputType = keyof typeof CUSTOM_FILTER_INPUT_TYPES;
type CustomFilterMatch = keyof typeof CUSTOM_FILTER_MATCH;

type SelectOption = {
    label: string;
    value: string;
};

type CustomFilterConfig = {
    tab: CustomFilterTab;
    entity: CustomFilterEntity;
    field: string[];
    title: string;
    placeholder: string;
    input_type: CustomFilterInputType;
    match: CustomFilterMatch;
};

const cleanString = (text: string): string => {
    return text.replace(/\s+/, " ").trim().toLowerCase();
};

const filterOption = (option: SelectOption, input: string): boolean => {
    if (input !== "") {
        const needle = cleanString(input);
        const haystack = cleanString(option.label);
        return option.value === needle || haystack.includes(needle);
    }

    return true;
};

export const parseFilterConfigs = (content: string, plant_external_id?: string): CustomFilterConfig[] => {
    const filter_configs: CustomFilterConfig[] = [];
    if (plant_external_id === undefined) {
        return filter_configs;
    }

    try {
        const settings_format_error = "Invalid filter settings format";
        const raw_settings = JSON.parse(content);
        if (!Array.isArray(raw_settings)) {
            console.error(settings_format_error);
            return filter_configs;
        }

        const plant_settings = raw_settings.find(
            rs => typeof rs === "object" && rs.plant_external_id === plant_external_id
        );
        if (plant_settings === undefined) {
            return filter_configs;
        }

        if (!Array.isArray(plant_settings.filters)) {
            console.error(settings_format_error);
            return filter_configs;
        }

        for (const settings of plant_settings.filters) {
            if (typeof settings !== "object") {
                console.error(settings_format_error, settings);
                continue;
            }

            const tab = settings.tab;
            if (!Object.keys(CUSTOM_FILTER_TABS).includes(tab)) {
                console.error("Invalid custom filter `tab` value", tab);
                continue;
            }

            const entity = settings.entity;
            if (!Object.keys(CUSTOM_FILTER_ENTITIES).includes(entity)) {
                console.error("Invalid custom filter `entity` value", entity);
                continue;
            }

            const field = settings.field;
            if (!Array.isArray(field) || field.some(f => typeof f !== "string")) {
                console.error("Invalid custom filter `field` format", field);
                continue;
            }

            const title = settings.title;
            if (typeof title !== "string") {
                console.error("Invalid custom filter `title` format", title);
                continue;
            }

            const placeholder = settings.placeholder;
            if (typeof placeholder !== "string") {
                console.error("Invalid custom filter `placeholder` format", title);
                continue;
            }

            const input_type = settings.input_type ?? CUSTOM_FILTER_INPUT_TYPES.select;
            if (!Object.keys(CUSTOM_FILTER_INPUT_TYPES).includes(input_type)) {
                console.error("Invalid custom filter `input_type` value", input_type);
                continue;
            }

            const match = settings.match ?? CUSTOM_FILTER_MATCH.exact;
            if (!Object.keys(CUSTOM_FILTER_MATCH).includes(match)) {
                console.error("Invalid custom filter `match` value", match);
                continue;
            }

            filter_configs.push({
                tab,
                entity,
                field,
                match,
                title,
                placeholder,
                input_type,
            });
        }
    } catch (error) {
        console.error("Failed to parse planning table filter configurations:", error);
    }

    return filter_configs;
};

/* -------------------------------------------------------------------------------------------------
 * CustomFilterWrapper
 * -----------------------------------------------------------------------------------------------*/

type CustomFilterWrapperProps = {
    children: React.ReactNode;
};

const CustomFilterWrapper = (props: CustomFilterWrapperProps) => {
    const { children } = props;
    return <div className="custom-filter">{children}</div>;
};

/* -------------------------------------------------------------------------------------------------
 * CustomFilter
 * -----------------------------------------------------------------------------------------------*/

type CustomFilterProps = {
    entity: CustomFilterEntity;
    field: string;
    input_type: CustomFilterInputType;
    match: CustomFilterMatch;
    palceholder: string;
};

const CustomFilter = (props: CustomFilterProps) => {
    const { entity, field, input_type, match, palceholder } = props;
    const dispatch: UseDispatchHook<PlanningTableFiltersActionTypes> = useDispatch();
    const { lines_orders, unscheduled_orders } = useSelector((state: ReduxState) => state.gantt_chart_lines_orders);
    const { materials } = useSelector((state: ReduxState) => state.gantt_chart);
    const selected_values = useSelector((state: ReduxState) => state.gantt_chart_filters.selected_custom_filter_values);

    const options = useMemo((): SelectOption[] => {
        if (![CUSTOM_FILTER_INPUT_TYPES.select, CUSTOM_FILTER_INPUT_TYPES.multiselect].includes(input_type)) {
            return [];
        }

        const select_options: SelectOption[] = [];
        const values_set: Set<string> = new Set();
        const is_tag_field = field.startsWith("tags.");
        const field_name = is_tag_field ? field.slice(5) : field;
        if (entity === CUSTOM_FILTER_ENTITIES.material) {
            for (const material of materials) {
                const field_value = is_tag_field ? material.tags[field_name] : material[field_name];
                if (field_value === undefined) {
                    continue;
                }

                const field_value_string = String(field_value).trim();
                if (!values_set.has(field_value_string)) {
                    values_set.add(field_value_string);
                    select_options.push({
                        label: field_value_string,
                        value: field_value_string,
                    });
                }
            }
        } else if (entity === CUSTOM_FILTER_ENTITIES.order) {
            for (const line_order of [...lines_orders, ...unscheduled_orders]) {
                for (const order of line_order.orders) {
                    const field_value = is_tag_field ? order.tags[field_name] : order[field_name];
                    if (field_value === undefined) {
                        continue;
                    }

                    const field_value_string = String(field_value).trim();
                    if (!values_set.has(field_value_string)) {
                        select_options.push({
                            label: field_value_string,
                            value: field_value_string,
                        });
                        values_set.add(field_value_string);
                    }
                }
            }
        }

        select_options.sort((a, b) => a.label.localeCompare(b.label));
        return select_options;
    }, [entity, field, input_type, lines_orders, unscheduled_orders, materials]);

    useEffect(() => {
        const data: SelectedCustomFilterConfig = {
            selected_custom_filter_entity: entity,
            selected_custom_filter_field: field,
            selected_custom_filter_input_type: input_type,
            selected_custom_filter_match: match,
        };
        dispatch({
            type: SET_SELECTED_CUSTOM_FILTER_CONFIG,
            data,
        });
    }, [dispatch, entity, field, input_type, options]);

    const handleDateTimeChange = useCallback((date: Date) => {
        dispatch({
            type: SET_SELECTED_CUSTOM_FILTER_VALUES,
            data: [String(date.getTime())],
        });
    }, []);

    const handleDateTimeClear = useCallback(() => {
        dispatch({
            type: SET_SELECTED_CUSTOM_FILTER_VALUES,
            data: [],
        });
    }, []);

    const handleMultiSelectChange = useCallback((options?: SelectOption[] | null) => {
        if (!options) {
            dispatch({ type: RESET_FILTERS, data: undefined });
        } else {
            const values = options.map(option => option.value);
            dispatch({
                type: SET_SELECTED_CUSTOM_FILTER_VALUES,
                data: values,
            });
        }
    }, []);

    const handleSelectChange = useCallback((option?: SelectOption | null) => {
        if (!option) {
            dispatch({ type: RESET_FILTERS, data: undefined });
        } else {
            dispatch({
                type: SET_SELECTED_CUSTOM_FILTER_VALUES,
                data: [option.value],
            });
        }
    }, []);

    if (input_type === CUSTOM_FILTER_INPUT_TYPES.datetime) {
        return (
            <CustomFilterWrapper>
                <DateTimePicker
                    value={selected_values[0] && new Date(Number(selected_values[0]))}
                    placeholder={palceholder}
                    pickerType="date-time"
                    popupPostion="fixed"
                    onChange={handleDateTimeChange}
                    onClear={handleDateTimeClear}
                />
            </CustomFilterWrapper>
        );
    }

    if (input_type === CUSTOM_FILTER_INPUT_TYPES.multiselect) {
        return (
            <CustomFilterWrapper>
                <MultiSelect
                    value={selected_values.map(value => ({ label: value, value }))}
                    placeholder={palceholder}
                    options={options}
                    onChange={handleMultiSelectChange}
                    filterOption={filterOption}
                    isSearchable
                    isClearable
                />
            </CustomFilterWrapper>
        );
    }

    return (
        <CustomFilterWrapper>
            <Select
                value={selected_values.map(value => ({ label: value, value }))}
                placeholder={palceholder}
                options={options}
                onChange={handleSelectChange}
                filterOption={filterOption}
                isSearchable
                isClearable
            />
        </CustomFilterWrapper>
    );
};

export default CustomFilter;

export { CUSTOM_FILTER_TABS, CUSTOM_FILTER_ENTITIES, CUSTOM_FILTER_INPUT_TYPES, CUSTOM_FILTER_MATCH };

export type {
    CustomFilterConfig,
    CustomFilterProps,
    CustomFilterTab,
    CustomFilterEntity,
    CustomFilterInputType,
    CustomFilterMatch,
    SelectOption,
};
